import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TextInput from "../../components/ui/TextInput";
import { useSortableSensors } from "../../hooks/useSortableSensors";
import Tooltip from "@mui/material/Tooltip";
import {
    getActivityPCVTypes,
    createActivityPCVType,
    updateActivityPCVType,
    deleteActivityPCVType,
    reorderActivityPCVTypes,
    resolveTypeName,
    ActivityPCVType,
} from "../../api/activityPCVType";
import i18n from "../../i18n";
import styles from "./ActivityPCVTypesModal.module.css";

interface ActivityPCVTypesModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Called after any change so the parent can refresh its type options. */
    onChanged?: () => void;
}

interface SortableTypeItemProps {
    type: ActivityPCVType;
    index: number;
    loading: boolean;
    isEditing: boolean;
    editName: string;
    setEditName: (v: string) => void;
    onStartEdit: (type: ActivityPCVType) => void;
    onCancelEdit: () => void;
    onSaveEdit: (typeId: string) => void;
    onDelete: (typeId: string, typeName: string) => void;
    t: any;
}

const SortableTypeItem: React.FC<SortableTypeItemProps> = ({
    type,
    index,
    loading,
    isEditing,
    editName,
    setEditName,
    onStartEdit,
    onCancelEdit,
    onSaveEdit,
    onDelete,
    t,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: type.id,
        disabled: isEditing,
    });

    const displayName = resolveTypeName(type, i18n.language) || t("unnamed");

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    if (isEditing) {
        return (
            <div ref={setNodeRef} style={style} className={styles.tagItem}>
                <div className={styles.editForm}>
                    <TextInput
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder={t("enterActivityTypeName")}
                        size="small"
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter" && editName.trim()) {
                                onSaveEdit(type.id);
                            }
                            if (e.key === "Escape") {
                                onCancelEdit();
                            }
                        }}
                    />
                    <div className={styles.editActions}>
                        <button className={styles.cancelButton} onClick={onCancelEdit} disabled={loading}>
                            {t("cancel")}
                        </button>
                        <button
                            className={styles.saveButton}
                            onClick={() => onSaveEdit(type.id)}
                            disabled={loading || !editName.trim()}
                        >
                            {t("save")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div ref={setNodeRef} style={style} className={styles.tagItem}>
            <button
                type="button"
                className={styles.dragHandle}
                title={t("dragToReorder")}
                {...attributes}
                {...listeners}
            >
                <span className={styles.indexBadge}>{index + 1}</span>
                <span className={styles.dragDots}>⠿</span>
            </button>
            <div className={styles.tagInfo}>
                <span className={styles.tagName}>{displayName}</span>
                <span className={styles.tagCount}>
                    ({type.activityCount || 0} {t("activities")})
                </span>
            </div>
            <div className={styles.tagActions}>
                <button className={styles.actionButton} onClick={() => onStartEdit(type)} disabled={loading}>
                    {t("edit")}
                </button>
                <button
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={() => onDelete(type.id, displayName)}
                    disabled={loading || (type.activityCount || 0) > 0}
                    title={(type.activityCount || 0) > 0 ? t("cannotDeleteActivityTypeWithActivities") : t("delete")}
                >
                    {t("delete")}
                </button>
            </div>
        </div>
    );
};

const ActivityPCVTypesModal: React.FC<ActivityPCVTypesModalProps> = ({ isOpen, onClose, onChanged }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);
    const token = localStorage.getItem("token") || "";

    const [types, setTypes] = useState<ActivityPCVType[]>([]);
    const [loading, setLoading] = useState(false);
    const [newTypeName, setNewTypeName] = useState("");
    const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [error, setError] = useState<string | null>(null);

    const sourceLanguage = (i18n.language || "EN").toUpperCase();
    const sensors = useSortableSensors();

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    const fetchTypes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getActivityPCVTypes({ token });
            setTypes(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("Error fetching activity types:", err);
            setError(t("errorFetchingActivityTypes"));
        } finally {
            setLoading(false);
        }
    }, [token, t]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            fetchTypes();
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick, fetchTypes]);

    const notifyChanged = () => {
        if (onChanged) onChanged();
    };

    const handleCreateType = async () => {
        if (!newTypeName.trim()) return;
        setLoading(true);
        setError(null);
        try {
            await createActivityPCVType({ token, name: newTypeName.trim(), language: sourceLanguage });
            setNewTypeName("");
            await fetchTypes();
            notifyChanged();
        } catch (err) {
            console.error("Error creating activity type:", err);
            setError(t("errorCreatingActivityType"));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateType = async (typeId: string) => {
        if (!editName.trim()) return;
        setLoading(true);
        setError(null);
        try {
            await updateActivityPCVType({ token, typeId, newName: editName.trim(), language: sourceLanguage });
            setEditingTypeId(null);
            setEditName("");
            await fetchTypes();
            notifyChanged();
        } catch (err) {
            console.error("Error updating activity type:", err);
            setError(t("errorUpdatingActivityType"));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteType = async (typeId: string, typeName: string) => {
        if (!window.confirm(t("confirmDeleteActivityType", { name: typeName }))) return;
        setLoading(true);
        setError(null);
        try {
            await deleteActivityPCVType({ token, typeId });
            await fetchTypes();
            notifyChanged();
        } catch (err: any) {
            console.error("Error deleting activity type:", err);
            if (err?.statusCode === 409) {
                setError(t("cannotDeleteActivityTypeWithActivities"));
            } else {
                setError(t("errorDeletingActivityType"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = types.findIndex((ty) => ty.id === active.id);
        const newIndex = types.findIndex((ty) => ty.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(types, oldIndex, newIndex);
        // Optimistic update so the list reflects the new order immediately.
        setTypes(reordered);
        setError(null);
        try {
            await reorderActivityPCVTypes({ token, orderedIds: reordered.map((ty) => ty.id) });
            notifyChanged();
        } catch (err) {
            console.error("Error reordering activity types:", err);
            setError(t("errorUpdatingActivityType"));
            await fetchTypes();
        }
    };

    const startEditing = (type: ActivityPCVType) => {
        setEditingTypeId(type.id);
        setEditName(resolveTypeName(type, i18n.language));
    };

    const cancelEditing = () => {
        setEditingTypeId(null);
        setEditName("");
    };

    if (!isOpen) return null;

    const modal = (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} ref={modalContentRef}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{t("manageActivityTypes")}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <div className={styles.createSection}>
                        <h3 className={styles.sectionTitle}>
                            {t("createNewActivityType")}
                            <Tooltip title={t("doNotTranslateTooltip")} arrow>
                                <span className="tooltip-icon" style={{ marginLeft: 8, marginBottom: 0 }}>
                                    i
                                </span>
                            </Tooltip>
                        </h3>
                        <div className={styles.createForm}>
                            <div className={styles.createInput}>
                                <TextInput
                                    placeholder={t("enterActivityTypeName")}
                                    value={newTypeName}
                                    onChange={(e) => setNewTypeName(e.target.value)}
                                    size="small"
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        if (e.key === "Enter" && newTypeName.trim()) {
                                            handleCreateType();
                                        }
                                    }}
                                />
                            </div>
                            <button
                                className={styles.saveButton}
                                onClick={handleCreateType}
                                disabled={loading || !newTypeName.trim()}
                            >
                                {t("create")}
                            </button>
                        </div>
                    </div>

                    <div className={styles.tagsSection}>
                        <h3 className={styles.sectionTitle}>{t("existingActivityTypes")}</h3>
                        {loading && types.length === 0 ? (
                            <div className={styles.loading}>{t("loading")}</div>
                        ) : types.length === 0 ? (
                            <div className={styles.emptyState}>{t("noActivityTypesYet")}</div>
                        ) : (
                            <>
                                <p className={styles.reorderHint}>{t("reorderActivityTypesHint")}</p>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={types.map((ty) => ty.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className={styles.tagsList}>
                                            {types.map((type, index) => (
                                                <SortableTypeItem
                                                    key={type.id}
                                                    type={type}
                                                    index={index}
                                                    loading={loading}
                                                    isEditing={editingTypeId === type.id}
                                                    editName={editName}
                                                    setEditName={setEditName}
                                                    onStartEdit={startEditing}
                                                    onCancelEdit={cancelEditing}
                                                    onSaveEdit={handleUpdateType}
                                                    onDelete={handleDeleteType}
                                                    t={t}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default ActivityPCVTypesModal;
